import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Users, Shield, Smartphone, Search, FileDown, Zap } from 'lucide-react';

const FeaturesContainer = styled.section`
  padding: 5rem 2rem;
  background: #0a0a0c;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #39c0ed, #7a43e5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.25rem;
  color: #b0b0b0;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.3s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(57, 192, 237, 0.3);
    transform: translateY(-5px);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #39c0ed, #7a43e5);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #fff;
`;

const FeatureDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
`;

const features = [
  {
    icon: <Users size={28} />,
    title: "Smart Contact Organization",
    description: "Intelligently organize your contacts with custom categories, tags, and groups for easy access and management."
  },
  {
    icon: <Search size={28} />,
    title: "Quick Search & Filter",
    description: "Find any contact instantly with powerful search capabilities and advanced filtering options."
  },
  {
    icon: <Shield size={28} />,
    title: "Secure & Private",
    description: "Your contacts are encrypted and stored securely. We prioritize your privacy above all else."
  },
  {
    icon: <Smartphone size={28} />,
    title: "Mobile First Design",
    description: "Built specifically for Android with a clean, intuitive interface that works seamlessly on all devices."
  },
  {
    icon: <FileDown size={28} />,
    title: "Easy Export & Import",
    description: "Seamlessly export your contacts to various formats and import from multiple sources with just a few taps."
  },
  {
    icon: <Zap size={28} />,
    title: "Lightning Fast",
    description: "Optimized performance ensures smooth operation even with thousands of contacts."
  }
];

const Features: React.FC = () => {
  return (
    <FeaturesContainer id="features">
      <Content>
        <SectionTitle>Why Contact Manager?</SectionTitle>
        <SectionSubtitle>
          Experience the next generation of contact management with features designed for modern professionals
        </SectionSubtitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <IconWrapper>{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Content>
    </FeaturesContainer>
  );
};

export default Features;
